import { createContext } from 'react';

// Thread-owned cancellation for the initial request and mount-time reconnect.
// Voice handoff aborts this transport directly: assistant-ui cancelRun schedules
// a delayed repository resync that can overwrite an immediately appended turn.
export function createResumeCancellation() {
  let active: AbortController | null = null;
  let clock = 0;
  let stoppedAt = 0;
  function acquire(parent?: AbortSignal) {
    const controller = new AbortController();
    active = controller;
    const abort = () => controller.abort(parent?.reason);
    parent?.addEventListener('abort', abort, { once: true });
    if (parent?.aborted) abort();
    const release = () => {
      parent?.removeEventListener('abort', abort);
      if (active === controller) active = null;
    };
    controller.signal.addEventListener('abort', release, { once: true });
    return { controller, release };
  }
  function tracked<T>(stream: ReadableStream<T>, scope: ReturnType<typeof acquire>) {
    // The runtime supports cancel; this keeps compatibility with older DOM declarations.
    const transformer = { flush: scope.release, cancel: scope.release };
    return stream.pipeThrough(new TransformStream<T, T>(transformer), {
      signal: scope.controller.signal,
    });
  }
  return {
    wrap<T>(stream: ReadableStream<T>): ReadableStream<T> {
      return tracked(stream, acquire());
    },
    async request<T>(
      send: (signal: AbortSignal) => Promise<ReadableStream<T>>,
      parent?: AbortSignal,
    ) {
      const scope = acquire(parent);
      try {
        scope.controller.signal.throwIfAborted();
        return tracked(await send(scope.controller.signal), scope);
      } catch (error) {
        scope.release();
        throw error;
      }
    },
    cancel() {
      stoppedAt = ++clock;
      active?.abort();
      active = null;
    },
    /** Marks the start of a request or stream (the person's Stop applies only to what began BEFORE it). */
    begin() {
      return ++clock;
    },
    /**
     * Whether the person pressed Stop after `since` began. The server stops the RUN on DELETE, so a stream that is
     * merely dropped (unload, tab close, navigation, unmount) must not send it: the build keeps going and the next
     * view re-attaches. A Stop belongs to the requests alive when it was pressed, never to a later original.
     */
    stoppedSince(since: number) {
      return stoppedAt > since;
    },
  };
}

export const ResumeCancellationContext = createContext<() => void>(() => {});
